import React, { useState, useEffect, useRef } from 'react';
import {
  Calendar,
  Search,
  Filter,
  AlertCircle,
  User,
  Share2,
  Gift
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Lottie from 'lottie-react';
import loadingAnimation from '../assets/Loading.json';
import toast from 'react-hot-toast';
import { PERMISSIONS, usePermissionHook, PERMISSION_DENIED_MESSAGE, permissionGuardClass } from '../utils/permissions';
import PagePermissionWrapper from '../components/PagePermissionWrapper';
import CampaignCard from '../components/CampaignCard';
import CampaignTypeSwitcher from '../components/CampaignTypeSwitcher';
import { LimelightNav } from '../components/ui/limelight-nav';
import { Dropdown } from '../components/ui/dropdown-01';
import EditCampaignModal from '../components/EditCampaignModal';
import { ModalReporter } from '../context/ModalContext';
import {
  fetchPersonalCampaigns,
  fetchReferralCampaigns,
  fetchGiftCards,
  fetchGeneralCampaigns,
  updatePersonalCampaign,
  updateReferralCampaign,
  updateGiftCard,
  updateGeneralCampaign,
  reorderGeneralCampaigns,
  reorderPersonalCampaigns
} from '../redux/slices/campaignSlice';
import { fetchGeneralTemplates, fetchPersonalTemplates } from '../redux/slices/campaignTemplateSlice';
import { api } from '../redux/api/axiosManager';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { campaignTitleMatchesSearch } from '../utils/campaignTypeDisplay';

// Tarih dönüştürücü - .NET formatından dd.mm.yyyy'ye
const convertDateFromAPI = (dateString) => {
  if (!dateString) return '-';
  try {
    const timestamp = dateString.match(/\d+/)?.[0];
    if (!timestamp) return '-';
    const date = new Date(parseInt(timestamp));
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  } catch (error) {
    return '-';
  }
};

// YYYY/MM/DD formatını ve geçerli tarihi kontrol et
const isValidDateInput = (value) => {
  if (!value) return true; // boş ise optional
  const regex = /^\d{4}\/\d{2}\/\d{2}$/;
  if (!regex.test(value)) return false;
  const parsed = new Date(value.replaceAll('/', '-'));
  return !Number.isNaN(parsed.getTime());
};

// API'den gelen dd.mm.yyyy formatını input için YYYY/MM/DD'ye çevir
const toInputDate = (value) => {
  if (!value || value === '-') return '';
  const match = value.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (!match) return '';
  const [, day, month, year] = match;
  return `${year}/${month}/${day}`;
};

const toPriorityNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? num : Number.MAX_SAFE_INTEGER;
};

const compareByPriorityAsc = (a, b) => {
  const pa = toPriorityNumber(a?.priority);
  const pb = toPriorityNumber(b?.priority);
  if (pa !== pb) return pa - pb;
  // Priority eşitse mevcut görünümde tutarlı bir fallback
  return String(a?.title || a?.campaignTitle || '').localeCompare(
    String(b?.title || b?.campaignTitle || ''),
    'tr-TR'
  );
};

const getCampaignSortId = (campaign, scope) => {
  if (scope === 'general') {
    return `general-${campaign.discountOfferCode || campaign.id}`;
  }
  return `personal-${campaign.attributeCode || campaign.id}-${campaign.discountOfferCode || ''}`;
};

const SortableCampaignCard = ({ campaign, sortId, ...cardProps }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sortId });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners} className="touch-none">
      <CampaignCard campaign={campaign} sortingMode {...cardProps} />
    </div>
  );
};

const CampaignList = ({ type = 'personal' }) => { // Props'tan type al (personal veya general)
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [activeSubTab, setActiveSubTab] = useState('personal'); // 'personal', 'referral', 'gift'
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [editModal, setEditModal] = useState({ show: false, campaign: null });
  const [isDragging, setIsDragging] = useState(false);

  // Redux state
  const {
    personalCampaigns,
    personalLoading,
    personalError,
    referralCampaigns,
    referralLoading,
    referralError,
    giftCards,
    giftCardsLoading,
    giftCardsError,
    generalCampaigns,
    generalLoading,
    generalError
  } = useSelector(state => state.campaigns);

  const { generalTemplates, personalTemplates } = useSelector((state) => state.campaignTemplates || { generalTemplates: [], personalTemplates: [] });

  // Auth state'inden user bilgisi
  const { user } = useSelector(state => state.auth);
  const canManageCampaigns = usePermissionHook(PERMISSIONS.CAMPAIGNS_MANAGE);


  const handleOpenEdit = (campaign) => {
    if (!canManageCampaigns) {
      toast.error(PERMISSION_DENIED_MESSAGE);
      return;
    }
    setEditModal({ show: true, campaign });
  };

  // Local state
  const hasFetched = useRef(false);
  const hasReferralFetched = useRef(false);
  const hasGiftCardsFetched = useRef(false);
  const [formData, setFormData] = useState({
    campaignImage: '',
    campaignImage2: '', // ✅ Kiosk küçük banner görseli (genel ve kişisel kampanyalar için)
    campaignImage3: '', // ✅ Kiosk büyük banner görseli (sadece genel kampanyalar için)
    campaignTitle: '',
    campaignContent: '',
    campaignContentShort: '',
    campaignFeatures: '',
    campaignTermsAndConditions: '',
    overwriteStartDate: '',
    overwriteEndDate: '',
    hideShowEndDate: 0,
    priority: ''
  });

  // Error mesajını string'e çevir
  const getErrorMessage = (error) => {
    if (!error) return '';
    if (typeof error === 'string') return error;
    if (error.message) return error.message;
    if (error.error) return error.error;
    if (typeof error === 'object') {
      // Obje ise önemli alanları kontrol et
      if (error.success !== undefined && error.message) {
        return error.message;
      }
      return JSON.stringify(error);
    }
    return 'Bilinmeyen hata';
  };

  const matchesFilter = (campaign) => {
    // Status filter
    const isActive = campaign.isActiveApp === 1 || campaign.status === 'active';
    const matchesStatus =
      filterStatus === 'all'
        ? true
        : filterStatus === 'active'
          ? isActive
          : !isActive;

    if (!matchesStatus) {
      return false;
    }

    return campaignTitleMatchesSearch(campaign, searchTerm);
  };

  const filteredPersonalCampaigns = personalCampaigns.filter(matchesFilter).slice().sort(compareByPriorityAsc);
  const filteredReferralCampaigns = referralCampaigns.filter(matchesFilter).slice().sort(compareByPriorityAsc);
  const filteredGiftCards = giftCards.filter(matchesFilter).slice().sort(compareByPriorityAsc);
  const filteredGeneralCampaigns = generalCampaigns.filter(matchesFilter).slice().sort(compareByPriorityAsc);
  
  // ✅ Her buton için ayrı loading state'i
  const [updateLoading, setUpdateLoading] = useState(false); // Değişiklikleri kaydet
  const [publishLoading, setPublishLoading] = useState(false); // Yayınla
  const [unpublishLoading, setUnpublishLoading] = useState(false); // Yayından kaldır
  const [originalFormData, setOriginalFormData] = useState(null);
  const [emptyFields, setEmptyFields] = useState([]);
  const [isSortMode, setIsSortMode] = useState(false);
  const [sortDraft, setSortDraft] = useState([]);
  const [sortSaving, setSortSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    })
  );

  const isPublishReadyWithImages = () => {
    const hasMainImage = !!formData.campaignImage?.trim();
    const hasSmallKioskImage = !!formData.campaignImage2?.trim();
    const hasLargeKioskImage = !!formData.campaignImage3?.trim();

    if (type === 'general') {
      return hasMainImage && hasSmallKioskImage && hasLargeKioskImage;
    }

    // Bana Özel > Kişisel kampanyalarda küçük kiosk görseli de zorunlu.
    if (type === 'personal' && activeSubTab === 'personal') {
      return hasMainImage && hasSmallKioskImage;
    }

    // Referans/Hediye kampanyalarında sadece ana görsel aranır.
    return hasMainImage;
  };

  const isSortableScope =
    type === 'general' || (type === 'personal' && activeSubTab === 'personal');

  const visibleSortableCampaigns = type === 'general'
    ? filteredGeneralCampaigns
    : filteredPersonalCampaigns;

  const currentSortScope = type === 'general' ? 'general' : 'personal';

  const handleStartSortMode = () => {
    if (!isSortableScope) return;
    setSortDraft(visibleSortableCampaigns);
    setIsSortMode(true);
  };

  const handleCancelSortMode = () => {
    setSortDraft([]);
    setIsSortMode(false);
  };

  const handleSortDragEnd = (event) => {
    const { active, over } = event;
    if (!active?.id || !over?.id || active.id === over.id) return;

    setSortDraft((prev) => {
      const oldIndex = prev.findIndex((item) => getCampaignSortId(item, currentSortScope) === active.id);
      const newIndex = prev.findIndex((item) => getCampaignSortId(item, currentSortScope) === over.id);
      if (oldIndex < 0 || newIndex < 0) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const handleSaveSortOrder = async () => {
    try {
      setSortSaving(true);
      if (currentSortScope === 'general') {
        const items = sortDraft.map((campaign, index) => ({
          discountOfferCode: campaign.discountOfferCode,
          priority: index + 1,
        }));
        await dispatch(reorderGeneralCampaigns(items)).unwrap();
        await dispatch(fetchGeneralCampaigns()).unwrap();
      } else {
        const items = sortDraft.map((campaign, index) => ({
          discountOfferCode: campaign.discountOfferCode,
          priority: index + 1,
        }));
        await dispatch(reorderPersonalCampaigns(items)).unwrap();
        await dispatch(fetchPersonalCampaigns()).unwrap();
      }

      toast.success('Kampanya sıralaması güncellendi');
      handleCancelSortMode();
    } catch (error) {
      toast.error(typeof error === 'string' ? error : (error?.message || 'Sıralama güncellenemedi'));
    } finally {
      setSortSaving(false);
    }
  };


  // Kişisel Kampanyaları Redux'tan çek
  useEffect(() => {
    if (type === 'personal' && activeSubTab === 'personal' && personalCampaigns.length === 0 && !personalLoading && !personalError) {
      dispatch(fetchPersonalCampaigns());
    }
  }, [type, activeSubTab, personalCampaigns.length, personalLoading, personalError, dispatch]);

  // Referans Çeklerini Redux'tan çek
  useEffect(() => {
    if (type === 'personal' && activeSubTab === 'referral' && referralCampaigns.length === 0 && !referralLoading && !referralError) {
      dispatch(fetchReferralCampaigns());
    }
  }, [type, activeSubTab, referralCampaigns.length, referralLoading, referralError, dispatch]);

  // Hediye Çeklerini Redux'tan çek
  useEffect(() => {
    if (type === 'personal' && activeSubTab === 'gift' && giftCards.length === 0 && !giftCardsLoading && !giftCardsError) {
      dispatch(fetchGiftCards());
    }
  }, [type, activeSubTab, giftCards.length, giftCardsLoading, giftCardsError, dispatch]);

  // Genel Kampanyaları Redux'tan çek
  useEffect(() => {
    if (type === 'general' && generalCampaigns.length === 0 && !generalLoading && !generalError) {
      dispatch(fetchGeneralCampaigns());
    }
  }, [type, generalCampaigns.length, generalLoading, generalError, dispatch]);

  // Taslak kampanyaları çek (kampanya düzenleme modalında kullanılmak üzere)
  useEffect(() => {
    if (type === 'general') {
      dispatch(fetchGeneralTemplates());
    } else {
      dispatch(fetchPersonalTemplates());
    }
  }, [type, dispatch]);

  useEffect(() => {
    if (!isSortMode) return;
    const currentIds = sortDraft.map((item) => getCampaignSortId(item, currentSortScope));
    const nextIds = visibleSortableCampaigns.map((item) => getCampaignSortId(item, currentSortScope));
    const currentSetKey = [...currentIds].sort().join('|');
    const nextSetKey = [...nextIds].sort().join('|');
    // Sadece kaynak listedeki öğe seti değiştiyse (yeni kayıt/silme) draft'i yenile.
    // Kullanıcının drag ile değiştirdiği sıralamayı burada ezmeyiz.
    if (currentSetKey !== nextSetKey) {
      setSortDraft(visibleSortableCampaigns);
    }
  }, [visibleSortableCampaigns, isSortMode, sortDraft, currentSortScope]);

  useEffect(() => {
    handleCancelSortMode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, activeSubTab, filterStatus, searchTerm]);

  // Modal açıldığında form'u doldur
  useEffect(() => {
    if (editModal.show && editModal.campaign) {
      const initialData = {
        campaignImage: editModal.campaign.campaignImage || editModal.campaign.image || '',
        campaignImage2: editModal.campaign.campaignImage2 || '', // ✅ Kiosk küçük banner görseli (genel ve kişisel kampanyalar için)
        campaignImage3: editModal.campaign.campaignImage3 || '', // ✅ Kiosk büyük banner görseli (sadece genel kampanyalar için)
        campaignTitle: editModal.campaign.campaignTitle || '',
        campaignContent: editModal.campaign.campaignContent || '',
        campaignContentShort: editModal.campaign.campaignContentShort || '',
        campaignFeatures: editModal.campaign.campaignFeatures || '',
        campaignTermsAndConditions: editModal.campaign.campaignTermsAndConditions || '',
        overwriteStartDate: toInputDate(editModal.campaign.overwriteStartDate),
        overwriteEndDate: toInputDate(editModal.campaign.overwriteEndDate),
        hideShowEndDate: editModal.campaign.hideShowEndDate ?? 0,
        priority: editModal.campaign.priority ?? ''
      };
      setFormData(initialData);
      // Orijinal veriyi sakla
      setOriginalFormData(initialData);
      // Boş alan listesini temizle
      setEmptyFields([]);
    }
  }, [editModal.show, editModal.campaign]);

  // Form değişti mi kontrol et
  const hasChanges = () => {
    if (!originalFormData) return false;
    return JSON.stringify(formData) !== JSON.stringify(originalFormData);
  };

  // Görsel dosyasını Base64'e çevir
  const handleImageUpload = (file, imageField = 'campaignImage') => {
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result;
      setFormData(prev => ({
        ...prev,
        [imageField]: base64String
      }));
      // Görsel yüklendiğinde boş alan listesinden çıkar
      if (emptyFields.includes(imageField)) {
        setEmptyFields(emptyFields.filter(field => field !== imageField));
      }
    };
    reader.readAsDataURL(file);
  };

  // Form input değişiklikleri
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'hideShowEndDate' ? (value === 'true' || value === 1 || value === '1' ? 1 : 0) : value
    }));
  };

  // Taslaktan forma uygula
  const handleApplyTemplate = (template) => {
    if (!template) return;
    const featuresForm = (template.campaignFeatures || '').split('#').filter(Boolean).join('\n');
    setFormData((prev) => ({
      ...prev,
      campaignImage: template.campaignImage || '',
      campaignImage2: template.campaignImage2 || '',
      campaignImage3: type === 'general' ? (template.campaignImage3 || '') : prev.campaignImage3,
      campaignTitle: template.campaignTitle || '',
      campaignContent: template.campaignContent || '',
      campaignContentShort: template.campaignContentShort || '',
      campaignFeatures: featuresForm,
      campaignTermsAndConditions: template.campaignTermsAndConditions || '',
    }));
    setEmptyFields([]);
    toast.success('Taslak uygulandı');
  };

  // Görseli kaldır
  const handleRemoveImage = (imageField = 'campaignImage') => {
    setFormData(prev => {
      const updated = {
        ...prev,
        [imageField]: ''
      };
      
      return updated;
    });
    
    // Boş alan listesinden çıkar (eğer varsa)
    if (emptyFields.includes(imageField)) {
      setEmptyFields(emptyFields.filter(field => field !== imageField));
    }
  };

  // Kampanya yayınlama (tüm kampanya türleri için)
  const handlePublishCampaign = async () => {
    if (!editModal.campaign) return;


    // Tüm alanların dolu olup olmadığını kontrol et
    const emptyFieldsList = [];

    if (!formData.campaignTitle || formData.campaignTitle.trim() === '') {
      emptyFieldsList.push('campaignTitle');
    }
    if (!formData.campaignContent || formData.campaignContent.trim() === '') {
      emptyFieldsList.push('campaignContent');
    }
    if (!formData.campaignFeatures || formData.campaignFeatures.trim() === '') {
      emptyFieldsList.push('campaignFeatures');
    }
    if (!formData.campaignImage || formData.campaignImage.trim() === '') {
      emptyFieldsList.push('campaignImage');
    }
    if (type === 'general' || (type === 'personal' && activeSubTab === 'personal')) {
      if (!formData.campaignImage2 || formData.campaignImage2.trim() === '') {
        emptyFieldsList.push('campaignImage2');
      }
    }
    if (type === 'general') {
      if (!formData.campaignImage3 || formData.campaignImage3.trim() === '') {
        emptyFieldsList.push('campaignImage3');
      }
    }

    // Eğer boş alan varsa border'ları kırmızı yap ve animasyon ekle
    if (emptyFieldsList.length > 0) {
      setEmptyFields(emptyFieldsList);
      toast.error('Lütfen tüm alanları doldurun', {
        duration: 4000,
      });
      // 3 saniye sonra kırmızı border'ları kaldır
      setTimeout(() => {
        setEmptyFields([]);
      }, 3000);
      return;
    }

    // Boş alan yoksa state'i temizle
    setEmptyFields([]);

    try {
      setPublishLoading(true); // ✅ Yayınla için ayrı loading

      const payload = {
        campaignImage: formData.campaignImage || '',
        campaignTitle: formData.campaignTitle || '',
        campaignContent: formData.campaignContent || '',
        campaignContentShort: formData.campaignContentShort || '',
        campaignFeatures: formData.campaignFeatures || '',
        campaignTermsAndConditions: formData.campaignTermsAndConditions || '',
        isActiveApp: 1, // Yayınla
        priority: formData.priority !== '' ? Number(formData.priority) : undefined
      };

      if (type === 'general') {
        // ✅ Kiosk görselleri - Boş string de gönderilmeli (silme için)
        payload.campaignImage2 = formData.campaignImage2 || '';
        payload.campaignImage3 = formData.campaignImage3 || ''; // ✅ Kiosk büyük banner görseli
        
        // Genel Kampanyalar
        const discountOfferCode = editModal.campaign.discountOfferCode;
        await dispatch(updateGeneralCampaign({ discountOfferCode, campaignData: payload }));
        
        // ✅ ETag cache'i temizle - İleride tüm liste çekilirse yeni veri gelsin
        api.clearETagCache('/api/admin/dashboard/campaigns/general');
        
        // ✅ Artık tüm listeyi çekmiyoruz - PUT response'undan gelen veri Redux state'ine eklendi
        // dispatch(fetchGeneralCampaigns()); // ❌ Kaldırıldı - Gereksiz network trafiği
      } else if (activeSubTab === 'personal') {
        // Kişisel Kampanyalar
        // ✅ Kiosk görseli - Boş string de gönderilmeli (silme için)
        payload.campaignImage2 = formData.campaignImage2 || '';
        
        const attributeCode = editModal.campaign.attributeCode;
        const discountOfferCode = editModal.campaign.discountOfferCode;
        await dispatch(updatePersonalCampaign({ attributeCode, discountOfferCode, campaignData: payload }));
        
        // ✅ ETag cache'i temizle - Güncelleme sonrası yeni veri gelsin
        api.clearETagCache('/api/admin/dashboard/campaigns/personal');
        
        // ✅ Artık tüm listeyi çekmiyoruz - PUT response'undan gelen veri Redux state'ine eklendi
        // dispatch(fetchPersonalCampaigns()); // ❌ Kaldırıldı - Gereksiz network trafiği
      } else if (activeSubTab === 'referral') {
        // Referans Çekleri
        const voucherCode = editModal.campaign.voucherCode;
        await dispatch(updateReferralCampaign({ voucherCode, campaignData: payload }));
        
        // ✅ ETag cache'i temizle - Güncelleme sonrası yeni veri gelsin
        api.clearETagCache('/api/admin/dashboard/campaigns/referral');
        
        // ✅ Artık tüm listeyi çekmiyoruz - PUT response'undan gelen veri Redux state'ine eklendi
        // dispatch(fetchReferralCampaigns()); // ❌ Kaldırıldı - Gereksiz network trafiği
      } else if (activeSubTab === 'gift') {
        // Hediye Çekleri
        const giftItemCode = editModal.campaign.giftItemCode;
        await dispatch(updateGiftCard({ giftItemCode, campaignData: payload }));
        
        // ✅ ETag cache'i temizle - Güncelleme sonrası yeni veri gelsin
        api.clearETagCache('/api/admin/dashboard/gift-cards');
        
        // ✅ Artık tüm listeyi çekmiyoruz - PUT response'undan gelen veri Redux state'ine eklendi
        // dispatch(fetchGiftCards()); // ❌ Kaldırıldı - Gereksiz network trafiği
      }

      toast.success('Kampanya başarıyla yayınlandı! ✅', {
        duration: 3000,
      });
      setEditModal({ show: false, campaign: null });

    } catch (error) {
      toast.error('Yayınlama sırasında bir hata oluştu: ' + (error.response?.data?.message || error.message), {
        duration: 4000,
      });
    } finally {
      setPublishLoading(false); // ✅ Yayınla için ayrı loading
    }
  };

  // Kampanya yayından kaldırma (tüm kampanya türleri için)
  const handleUnpublishCampaign = async () => {
    if (!editModal.campaign) return;


    // Değişiklik kaydetmede alan zorunluluğu yok.
    setEmptyFields([]);

    try {
      setUnpublishLoading(true); // ✅ Yayından kaldır için ayrı loading

      const payload = {
        campaignImage: formData.campaignImage || '',
        campaignTitle: formData.campaignTitle || '',
        campaignContent: formData.campaignContent || '',
        campaignContentShort: formData.campaignContentShort || '',
        campaignFeatures: formData.campaignFeatures || '',
        campaignTermsAndConditions: formData.campaignTermsAndConditions || '',
        isActiveApp: 0, // Yayından kaldır
        priority: formData.priority !== '' ? Number(formData.priority) : undefined
      };

      if (type === 'general') {
        // ✅ Kiosk görselleri - Boş string de gönderilmeli (silme için)
        payload.campaignImage2 = formData.campaignImage2 || '';
        payload.campaignImage3 = formData.campaignImage3 || ''; // ✅ Kiosk büyük banner görseli
        
        // Genel Kampanyalar
        const discountOfferCode = editModal.campaign.discountOfferCode;
        await dispatch(updateGeneralCampaign({ discountOfferCode, campaignData: payload }));
        
        // ✅ ETag cache'i temizle - İleride tüm liste çekilirse yeni veri gelsin
        api.clearETagCache('/api/admin/dashboard/campaigns/general');
        
        // ✅ Artık tüm listeyi çekmiyoruz - PUT response'undan gelen veri Redux state'ine eklendi
        // dispatch(fetchGeneralCampaigns()); // ❌ Kaldırıldı - Gereksiz network trafiği
      } else if (activeSubTab === 'personal') {
        // Kişisel Kampanyalar
        // ✅ Kiosk görseli - Boş string de gönderilmeli (silme için)
        payload.campaignImage2 = formData.campaignImage2 || '';
        
        const attributeCode = editModal.campaign.attributeCode;
        const discountOfferCode = editModal.campaign.discountOfferCode;
        await dispatch(updatePersonalCampaign({ attributeCode, discountOfferCode, campaignData: payload }));
        
        // ✅ ETag cache'i temizle - Güncelleme sonrası yeni veri gelsin
        api.clearETagCache('/api/admin/dashboard/campaigns/personal');
        
        // ✅ Artık tüm listeyi çekmiyoruz - PUT response'undan gelen veri Redux state'ine eklendi
        // dispatch(fetchPersonalCampaigns()); // ❌ Kaldırıldı - Gereksiz network trafiği
      } else if (activeSubTab === 'referral') {
        // Referans Çekleri
        const voucherCode = editModal.campaign.voucherCode;
        await dispatch(updateReferralCampaign({ voucherCode, campaignData: payload }));
        
        // ✅ ETag cache'i temizle - Güncelleme sonrası yeni veri gelsin
        api.clearETagCache('/api/admin/dashboard/campaigns/referral');
        
        // ✅ Artık tüm listeyi çekmiyoruz - PUT response'undan gelen veri Redux state'ine eklendi
        // dispatch(fetchReferralCampaigns()); // ❌ Kaldırıldı - Gereksiz network trafiği
      } else if (activeSubTab === 'gift') {
        // Hediye Çekleri
        const giftItemCode = editModal.campaign.giftItemCode;
        await dispatch(updateGiftCard({ giftItemCode, campaignData: payload }));
        
        // ✅ ETag cache'i temizle - Güncelleme sonrası yeni veri gelsin
        api.clearETagCache('/api/admin/dashboard/gift-cards');
        
        // ✅ Artık tüm listeyi çekmiyoruz - PUT response'undan gelen veri Redux state'ine eklendi
        // dispatch(fetchGiftCards()); // ❌ Kaldırıldı - Gereksiz network trafiği
      }

      toast.success('Kampanya yayından kaldırıldı! ✅', {
        duration: 3000,
      });
      setEditModal({ show: false, campaign: null });

    } catch (error) {
      toast.error('Yayından kaldırma sırasında bir hata oluştu: ' + (error.response?.data?.message || error.message), {
        duration: 4000,
      });
    } finally {
      setUnpublishLoading(false); // ✅ Yayından kaldır için ayrı loading
    }
  };

  // Kampanya güncelleme
  const handleUpdateCampaign = async () => {
    if (!editModal.campaign) return;
    // Değişiklik kaydetmede alan zorunluluğu yok.
    setEmptyFields([]);

    try {
      setUpdateLoading(true);

      // ✅ Mevcut kampanyanın durumunu koru (isActiveApp değerini ekle)
      // Düzenleme sırasında kampanyanın yayın durumu değişmemeli
      const camp = editModal.campaign;
      const computedIsActive = camp.isActiveApp !== undefined
        ? camp.isActiveApp
        : (camp.status === 'active' ? 1 : 0);
      const payload = {
        campaignImage: formData.campaignImage || '',
        campaignTitle: formData.campaignTitle || '',
        campaignContent: formData.campaignContent || '',
        campaignContentShort: formData.campaignContentShort || '',
        campaignFeatures: formData.campaignFeatures || '',
        campaignTermsAndConditions: formData.campaignTermsAndConditions || '',
        isActiveApp: computedIsActive,
        priority: formData.priority !== '' ? Number(formData.priority) : undefined
      };

      if (type === 'general') {
        // Tarih format kontrolü (YYYY/MM/DD)
        if (!isValidDateInput(formData.overwriteStartDate)) {
          toast.error('Başlangıç tarihi formatı YYYY/MM/DD olmalı', { duration: 3500 });
          setUpdateLoading(false);
          return;
        }
        if (!isValidDateInput(formData.overwriteEndDate)) {
          toast.error('Bitiş tarihi formatı YYYY/MM/DD olmalı', { duration: 3500 });
          setUpdateLoading(false);
          return;
        }
        // Yeni tarih override ve bitiş gizleme alanlarını ekle (opsiyonel)
        if (formData.overwriteStartDate?.trim()) {
          payload.overwriteStartDate = formData.overwriteStartDate.trim();
        }
        if (formData.overwriteEndDate?.trim()) {
          payload.overwriteEndDate = formData.overwriteEndDate.trim();
        }
        payload.hideShowEndDate = formData.hideShowEndDate ? 1 : 0;
        
        // ✅ Kiosk görselleri - Boş string de gönderilmeli (silme için)
        payload.campaignImage2 = formData.campaignImage2 || '';
        payload.campaignImage3 = formData.campaignImage3 || ''; // ✅ Kiosk büyük banner görseli

        // Genel Kampanyalar güncelleme
        const discountOfferCode = editModal.campaign.discountOfferCode;
        await dispatch(updateGeneralCampaign({ discountOfferCode, campaignData: payload }));

        // ✅ ETag cache'i temizle - İleride tüm liste çekilirse yeni veri gelsin
        api.clearETagCache('/api/admin/dashboard/campaigns/general');

        // ✅ Artık tüm listeyi çekmiyoruz - PUT response'undan gelen veri Redux state'ine eklendi
        // dispatch(fetchGeneralCampaigns()); // ❌ Kaldırıldı - Gereksiz network trafiği
      } else if (activeSubTab === 'referral') {
        // Tarih format kontrolü (YYYY/MM/DD)
        if (!isValidDateInput(formData.overwriteStartDate)) {
          toast.error('Başlangıç tarihi formatı YYYY/MM/DD olmalı', { duration: 3500 });
          setUpdateLoading(false);
          return;
        }
        if (!isValidDateInput(formData.overwriteEndDate)) {
          toast.error('Bitiş tarihi formatı YYYY/MM/DD olmalı', { duration: 3500 });
          setUpdateLoading(false);
          return;
        }
        // Yeni tarih override ve bitiş gizleme alanlarını ekle (opsiyonel)
        if (formData.overwriteStartDate?.trim()) {
          payload.overwriteStartDate = formData.overwriteStartDate.trim();
        }
        if (formData.overwriteEndDate?.trim()) {
          payload.overwriteEndDate = formData.overwriteEndDate.trim();
        }
        payload.hideShowEndDate = formData.hideShowEndDate ? 1 : 0;

        // Referans Çekleri güncelleme
        const voucherCode = editModal.campaign.voucherCode;
        await dispatch(updateReferralCampaign({ voucherCode, campaignData: payload }));

        // ✅ ETag cache'i temizle - Güncelleme sonrası yeni veri gelsin
        api.clearETagCache('/api/admin/dashboard/campaigns/referral');
        
        // ✅ Artık tüm listeyi çekmiyoruz - PUT response'undan gelen veri Redux state'ine eklendi
        // dispatch(fetchReferralCampaigns()); // ❌ Kaldırıldı - Gereksiz network trafiği
      } else if (activeSubTab === 'gift') {
        // Tarih format kontrolü (YYYY/MM/DD)
        if (!isValidDateInput(formData.overwriteStartDate)) {
          toast.error('Başlangıç tarihi formatı YYYY/MM/DD olmalı', { duration: 3500 });
          setUpdateLoading(false);
          return;
        }
        if (!isValidDateInput(formData.overwriteEndDate)) {
          toast.error('Bitiş tarihi formatı YYYY/MM/DD olmalı', { duration: 3500 });
          setUpdateLoading(false);
          return;
        }
        // Yeni tarih override ve bitiş gizleme alanlarını ekle (opsiyonel)
        if (formData.overwriteStartDate?.trim()) {
          payload.overwriteStartDate = formData.overwriteStartDate.trim();
        }
        if (formData.overwriteEndDate?.trim()) {
          payload.overwriteEndDate = formData.overwriteEndDate.trim();
        }
        payload.hideShowEndDate = formData.hideShowEndDate ? 1 : 0;

        // Hediye Çekleri güncelleme
        const giftItemCode = editModal.campaign.giftItemCode;
        await dispatch(updateGiftCard({ giftItemCode, campaignData: payload }));

        // ✅ ETag cache'i temizle - Güncelleme sonrası yeni veri gelsin
        api.clearETagCache('/api/admin/dashboard/gift-cards');
        
        // ✅ Artık tüm listeyi çekmiyoruz - PUT response'undan gelen veri Redux state'ine eklendi
        // dispatch(fetchGiftCards()); // ❌ Kaldırıldı - Gereksiz network trafiği
      } else {
        // Kişisel Kampanyalar güncelleme
        // Tarih format kontrolü (YYYY/MM/DD)
        if (!isValidDateInput(formData.overwriteStartDate)) {
          toast.error('Başlangıç tarihi formatı YYYY/MM/DD olmalı', { duration: 3500 });
          setUpdateLoading(false);
          return;
        }
        if (!isValidDateInput(formData.overwriteEndDate)) {
          toast.error('Bitiş tarihi formatı YYYY/MM/DD olmalı', { duration: 3500 });
          setUpdateLoading(false);
          return;
        }
        // Yeni tarih override ve bitiş gizleme alanlarını ekle (opsiyonel)
        if (formData.overwriteStartDate?.trim()) {
          payload.overwriteStartDate = formData.overwriteStartDate.trim();
        }
        if (formData.overwriteEndDate?.trim()) {
          payload.overwriteEndDate = formData.overwriteEndDate.trim();
        }
        payload.hideShowEndDate = formData.hideShowEndDate ? 1 : 0;

        // ✅ Kiosk görseli - Boş string de gönderilmeli (silme için)
        payload.campaignImage2 = formData.campaignImage2 || '';

        const attributeCode = editModal.campaign.attributeCode;
        const discountOfferCode = editModal.campaign.discountOfferCode;
        await dispatch(updatePersonalCampaign({ attributeCode, discountOfferCode, campaignData: payload }));

        // ✅ ETag cache'i temizle - İleride tüm liste çekilirse yeni veri gelsin
        api.clearETagCache('/api/admin/dashboard/campaigns/personal');

        // ✅ Artık tüm listeyi çekmiyoruz - PUT response'undan gelen veri Redux state'ine eklendi
        // dispatch(fetchPersonalCampaigns()); // ❌ Kaldırıldı - Gereksiz network trafiği
      }

      toast.success('Kampanya başarıyla güncellendi! ✅', {
        duration: 3000,
      });
      setEditModal({ show: false, campaign: null });

    } catch (error) {
      toast.error('Güncelleme sırasında bir hata oluştu: ' + (error.response?.data?.message || error.message), {
        duration: 4000,
      });
    } finally {
      setUpdateLoading(false);
    }
  };

  return (
    <>
      <ModalReporter open={editModal.show} />

      <PagePermissionWrapper
        requiredPermissions={[PERMISSIONS.CAMPAIGNS_READ]}
        pageName="Kampanya Yönetimi"
      >
        <div className="space-y-4">
          {/* Header */}
          <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
                {type === 'personal' ? 'Bana Özel Kampanyalar' : 'Genel Kampanyalar'}
              </h1>
              <p className="text-gray-500 mt-2 text-lg">
              {type === 'personal'
                ? 'Size özel kampanyaları görüntüleyin ve yönetin'
                : 'Tüm kullanıcılar için geçerli kampanyaları görüntüleyin'
              }
            </p>
            </div>
            <CampaignTypeSwitcher />
          </div>

          {/* Sub Tabs - Sadece Bana Özel sayfasında göster */}
          {type === 'personal' && (
            <LimelightNav
              items={[
                { id: 'personal', icon: <User />, label: 'Kişisel Kampanyalar' },
                { id: 'referral', icon: <Share2 />, label: 'Referans Çekleri' },
                { id: 'gift', icon: <Gift />, label: 'Hediye Çekleri' },
              ]}
              showLabels
              activeIndex={['personal', 'referral', 'gift'].indexOf(activeSubTab)}
              onTabChange={(i) => setActiveSubTab(['personal', 'referral', 'gift'][i])}
              className="bg-white border-gray-100"
              limelightClassName="bg-blue-600"
            />
          )}

          {/* Filters */}
          {(type === 'personal' || type === 'general') && (
            <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
              <div className="flex flex-col lg:flex-row gap-4 items-center">
                {/* Search */}
                <div className="flex-1 relative w-full">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    placeholder="Kampanya ara (isim, kod, açıklama)..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border-transparent focus:bg-white border focus:border-blue-200 rounded-2xl focus:outline-none focus:ring-4 focus:ring-blue-50/50 transition-all font-medium"
                  />
                </div>

                {/* Status Filter */}
                <div className="min-w-[200px]">
                  <Dropdown
                    iconLeft={<Filter size={18} />}
                    items={[
                      { value: 'all', label: 'Tüm Kampanyalar' },
                      { value: 'active', label: 'Yayında' },
                      { value: 'inactive', label: 'Yayında Değil' },
                    ]}
                    value={filterStatus}
                    onChange={setFilterStatus}
                    simple
                  />
                </div>

                {/* Date Filter (placeholder) */}
                {type === 'personal' && (
                  <div className="flex gap-2 items-center bg-gray-50 p-2 rounded-2xl border border-transparent hover:border-blue-100 transition-colors">
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                      <input
                        type="date"
                        className="pl-10 pr-3 py-2 bg-white rounded-xl text-sm font-medium text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-100"
                      />
                    </div>
                    <span className="text-gray-400 font-medium">-</span>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                      <input
                        type="date"
                        className="pl-10 pr-3 py-2 bg-white rounded-xl text-sm font-medium text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-100"
                      />
                    </div>
                  </div>
                )}

                {isSortableScope && (
                  <button
                    onClick={() => {
                      if (!canManageCampaigns) {
                        toast.error(PERMISSION_DENIED_MESSAGE);
                        return;
                      }
                      if (isSortMode) {
                        handleCancelSortMode();
                      } else {
                        handleStartSortMode();
                      }
                    }}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                      isSortMode
                        ? `bg-orange-100 text-orange-700 ${canManageCampaigns ? 'hover:bg-orange-200' : ''}`
                        : `bg-indigo-100 text-indigo-700 ${canManageCampaigns ? 'hover:bg-indigo-200' : ''}`
                    } ${permissionGuardClass(canManageCampaigns)}`}
                    aria-disabled={!canManageCampaigns}
                  >
                    {isSortMode ? 'Sıralamayı Kapat' : 'Sıralamayı Düzenle'}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Campaigns Grid - type ve activeSubTab'a göre içerik */}
          {type === 'personal' ? (
            // Bana Özel Kampanyalar - Alt sekmelere göre içerik
            activeSubTab === 'personal' ? (
              // Kişisel Kampanyalar
              personalLoading ? (
                <div className="flex items-center justify-center p-20">
                  <Lottie animationData={loadingAnimation} loop={true} className="w-48 h-48" />
                </div>
              ) : personalError ? (
                <div className="bg-red-50 rounded-2xl p-12 text-center border border-red-100">
                  <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-400" />
                  <h3 className="text-xl font-bold text-red-900 mb-2">Veri Yüklenemedi</h3>
                  <p className="text-red-600 mb-6">{getErrorMessage(personalError)}</p>
                  <button
                    onClick={() => dispatch(fetchPersonalCampaigns())}
                    className="px-8 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors shadow-lg shadow-red-200 font-medium"
                  >
                    Tekrar Dene
                  </button>
                </div>
              ) : filteredPersonalCampaigns.length === 0 ? (
                <div className="bg-white rounded-2xl p-12 text-center border border-gray-100 shadow-sm">
                  <Search className="w-16 h-16 mx-auto mb-4 text-gray-200" />
                  <p className="text-gray-500 text-lg font-medium">
                    Running a search?
                    {personalCampaigns.length === 0
                      ? ' Henüz kampanya bulunmuyor'
                      : ' Filtre kriterlerine uyan kampanya yok'}
                  </p>
                </div>
              ) : (
                isSortMode ? (
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleSortDragEnd}>
                    <SortableContext
                      items={sortDraft.map((campaign) => getCampaignSortId(campaign, 'personal'))}
                      strategy={rectSortingStrategy}
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-stretch">
                        {sortDraft.map((campaign, index) => (
                          <div key={getCampaignSortId(campaign, 'personal')} className="relative">
                            <div className="absolute top-2 left-2 z-10 bg-indigo-600 text-white text-xs px-2 py-1 rounded-full shadow">
                              #{index + 1}
                            </div>
                            <SortableCampaignCard
                              sortId={getCampaignSortId(campaign, 'personal')}
                              campaign={campaign}
                              onEdit={handleOpenEdit}
                              canEdit={canManageCampaigns}
                              useOverwriteDates={true}
                              showHideEndDate={false}
                            />
                          </div>
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-stretch">
                    {filteredPersonalCampaigns.map((campaign, index) => (
                      <CampaignCard
                        key={`personal-${campaign.attributeCode || ''}-${campaign.discountOfferCode || ''}-${index}`}
                        campaign={campaign}
                        onEdit={handleOpenEdit}
                        canEdit={canManageCampaigns}
                        useOverwriteDates={true}
                        showHideEndDate={false}
                      />
                    ))}
                  </div>
                )
              )
            ) : activeSubTab === 'referral' ? (
              // Referans Çekleri
              referralLoading ? (
                <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
                  <div className="flex justify-center mb-4">
                    <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                  <p className="text-gray-500 text-lg">Referans çekleri yükleniyor...</p>
                </div>
              ) : referralError ? (
                <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
                  <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
                    <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.694-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Veri Yüklenemedi</h3>
                  <p className="text-red-600 mb-2">{getErrorMessage(referralError)}</p>
                  <p className="text-sm text-gray-500 mb-4">
                    Bu genellikle backend izin sorunu nedeniyle oluşur. Yöneticinizle iletişime geçin.
                  </p>
                  <button
                    onClick={() => dispatch(fetchReferralCampaigns())}
                    className="mt-3 px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    Tekrar Dene
                  </button>
                </div>
              ) : filteredReferralCampaigns.length === 0 ? (
                <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
                  <p className="text-gray-500 text-lg">
                    {referralCampaigns.length === 0
                      ? 'Henüz referans çeki bulunmuyor'
                      : 'Filtre kriterlerine uyan referans çeki yok'}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-stretch">
                  {filteredReferralCampaigns.map((campaign, index) => (
                    <CampaignCard
                      key={`referral-${campaign.voucherCode || campaign.id || ''}-${index}`}
                      campaign={campaign}
                      onEdit={handleOpenEdit}
                      canEdit={canManageCampaigns}
                      useOverwriteDates={true}
                      showHideEndDate={false}
                    />
                  ))}
                </div>
              )
            ) : activeSubTab === 'gift' ? (
              // Hediye Çekleri
              giftCardsLoading ? (
                <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
                  <div className="flex justify-center mb-4">
                    <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                  <p className="text-gray-500 text-lg">Hediye çekleri yükleniyor...</p>
                </div>
              ) : giftCardsError ? (
                <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
                  <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
                    <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.694-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Veri Yüklenemedi</h3>
                  <p className="text-red-600 mb-2">{getErrorMessage(giftCardsError)}</p>
                  <p className="text-sm text-gray-500 mb-4">
                    Bu genellikle backend izin sorunu nedeniyle oluşur. Yöneticinizle iletişime geçin.
                  </p>
                  <button
                    onClick={() => dispatch(fetchGiftCards())}
                    className="mt-3 px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    Tekrar Dene
                  </button>
                </div>
              ) : filteredGiftCards.length === 0 ? (
                <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
                  <p className="text-gray-500 text-lg">
                    {giftCards.length === 0
                      ? 'Henüz hediye çeki bulunmuyor'
                      : 'Filtre kriterlerine uyan hediye çeki yok'}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-stretch">
                  {filteredGiftCards.map((campaign, index) => (
                    <CampaignCard
                      key={`gift-${campaign.giftItemCode || campaign.id || ''}-${index}`}
                      campaign={campaign}
                      onEdit={handleOpenEdit}
                      canEdit={canManageCampaigns}
                      useOverwriteDates={true}
                      showHideEndDate={false}
                    />
                  ))}
                </div>
              )
            ) : (
              // Genel Kampanyalar (Boş)
              <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
                <p className="text-gray-500 text-lg">Genel kampanyalar henüz hazır değil</p>
              </div>
            )
          ) : (
            // Genel Kampanyalar
            generalLoading ? (
              <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
                <div className="flex justify-center mb-4">
                  <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
                <p className="text-gray-500 text-lg">Genel kampanyalar yükleniyor...</p>
              </div>
            ) : generalError ? (
              <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
                  <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.694-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Veri Yüklenemedi</h3>
                <p className="text-red-600 mb-2">{getErrorMessage(generalError)}</p>
                <p className="text-sm text-gray-500 mb-4">
                  Bu genellikle backend izin sorunu nedeniyle oluşur. Yöneticinizle iletişime geçin.
                </p>
                <button
                  onClick={() => dispatch(fetchGeneralCampaigns())}
                  className="mt-3 px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  Tekrar Dene
                </button>
              </div>
            ) : filteredGeneralCampaigns.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
                <p className="text-gray-500 text-lg">
                  {generalCampaigns.length === 0
                    ? 'Henüz genel kampanya bulunmuyor'
                    : 'Filtre kriterlerine uyan genel kampanya yok'}
                </p>
              </div>
            ) : (
              isSortMode ? (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleSortDragEnd}>
                  <SortableContext
                    items={sortDraft.map((campaign) => getCampaignSortId(campaign, 'general'))}
                    strategy={rectSortingStrategy}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-stretch">
                      {sortDraft.map((campaign, index) => (
                        <div key={getCampaignSortId(campaign, 'general')} className="relative">
                          <div className="absolute top-2 left-2 z-10 bg-indigo-600 text-white text-xs px-2 py-1 rounded-full shadow">
                            #{index + 1}
                          </div>
                          <SortableCampaignCard
                            sortId={getCampaignSortId(campaign, 'general')}
                            campaign={campaign}
                            onEdit={handleOpenEdit}
                            canEdit={canManageCampaigns}
                            showStats={false}
                            useOverwriteDates={true}
                            showHideEndDate={false}
                          />
                        </div>
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-stretch">
                  {filteredGeneralCampaigns.map((campaign, index) => (
                    <CampaignCard
                      key={`general-${campaign.discountOfferCode || campaign.id || ''}-${index}`}
                      campaign={campaign}
                      onEdit={handleOpenEdit}
                      canEdit={canManageCampaigns}
                      showStats={false}
                      useOverwriteDates={true}
                      showHideEndDate={false}
                    />
                  ))}
                </div>
              )
            )
          )}

          {isSortMode && isSortableScope && (
            <div className="fixed bottom-6 right-6 z-[120] pointer-events-auto bg-white border border-gray-200 rounded-2xl shadow-xl p-3 flex items-center gap-3">
              <button
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancelSortMode();
                }}
                className="px-4 py-2 rounded-xl text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                disabled={sortSaving}
              >
                Vazgec
              </button>
              <button
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSaveSortOrder();
                }}
                className="px-4 py-2 rounded-xl text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 transition-colors disabled:opacity-60"
                disabled={sortSaving}
              >
                {sortSaving ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          )}

          {/* Edit Campaign Modal */}
          <EditCampaignModal
            show={editModal.show}
            campaign={editModal.campaign}
            formData={formData}
            setFormData={setFormData}
            emptyFields={emptyFields}
            setEmptyFields={setEmptyFields}
            isDragging={isDragging}
            setIsDragging={setIsDragging}
            onImageUpload={handleImageUpload}
            onRemoveImage={handleRemoveImage}
            onInputChange={handleInputChange}
            onUpdate={handleUpdateCampaign}
            onPublish={handlePublishCampaign}
            onUnpublish={handleUnpublishCampaign}
            onCancel={() => setEditModal({ show: false, campaign: null })}
            onApplyTemplate={handleApplyTemplate}
            templates={type === 'general' ? generalTemplates : personalTemplates}
            hasChanges={hasChanges()}
            canPublish={isPublishReadyWithImages()}
            updateLoading={updateLoading}
            publishLoading={publishLoading}
            unpublishLoading={unpublishLoading}
            type={type}
            activeSubTab={activeSubTab}
          />
        </div>
      </PagePermissionWrapper>
    </>
  );
};

export default CampaignList;

